import React, { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Grid, makeStyles, Paper, Theme } from "@material-ui/core";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Save, Send } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createItem,
  inputtingItem,
  resetItem,
  submitItem,
} from "../../redux/actions";
import {
  authSelector,
  createItemSelector,
  itemSelector,
} from "../../redux/selectors";
import { ModuleAppBar } from "../ModuleAppBar";
import { useParams } from "react-router-dom";
import { defaultValues, ScopeApplication } from "../../domain/ScopeApplication";
import { SectionProps } from "../sections/SectionProps";
import { ApplicationTitle } from "../sections/ApplicationTitle";
import { QuestionOne } from "../sections/QuestionOne";
import { QuestionTwo } from "../sections/QuestionTwo";
import { QuestionThree } from "../sections/QuestionThree";
import { QuestionFour } from "../sections/QuestionFour";
import { NewApplicationContent } from "./NewApplicationContent";
import { validationHelper } from "../common/validationHelper";
import { useOrganisationState } from "../../hooks/useOrganisationState";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      marginTop: theme.contentOffsets.AFTER_SECONDARY_APPBAR,
    },
    paper: {
      padding: 20,
      marginBottom: 30,
    },
    formTitle: {
      color: theme.palette.grey[500],
      fontSize: "1em",
      marginBottom: "15px",
      position: "relative",
      fontWeight: 600,
    },
    formHeading: {
      color: "#9f9f9f",
      fontSize: "1em",
      marginBottom: "30px",
      fontWeight: 600,
      position: "relative",
    },
    formSection: {
      padding: 30,
      boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
      border: "1px solid #ddd",
      backgroundColor: "#fff",
      borderRadius: "3px",
    },
  };
});

export const NewApplication: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(inputtingItem());
  }, [dispatch]);

  const classes = useStyles();
  const {
    handleSubmit,
    errors,
    control,
    formState,
    setError,
    clearErrors,
    getValues,
    setValue,
  } = useForm<ScopeApplication>({
    defaultValues: {
      ...defaultValues,
    },
  });

  const { title, questionTwo, questionThree, questionFour } = useWatch({
    control,
    name: ["title", "questionTwo", "questionThree", "questionFour"],
  });

  const { userType, token } = useSelector(authSelector);
  const { itemId } = useParams<{ itemId: string }>();

  const createState = useSelector(createItemSelector);

  const { scopes } = useSelector(itemSelector);
  const orgState = useOrganisationState(itemId);

  const { status } = createState;
  const disabled = status === "CREATING" || status === "SUBMITTING";

  const isQuestionFourRequired = scopes.find((x) => x.qualificationType === 25);

  const canSubmit =
    title !== null &&
    questionTwo !== null &&
    questionThree !== null &&
    scopes.length !== 0 &&
    (isQuestionFourRequired ? questionFour !== null : true);

  const onSave = (model: ScopeApplication) => {
    dispatch(createItem(itemId, model, scopes));
  };

  const onSubmit = (model: ScopeApplication) => {
    dispatch(submitItem(itemId, model, scopes));
  };
  const { lastCrudResult } = createState;

  useEffect(() => {
    validationHelper(lastCrudResult, (name, message) =>
      setError(name, { message })
    );
  }, [lastCrudResult, setError]);

  useEffect(() => {
    dispatch(resetItem());
  }, [dispatch]);

  const sectionProps: SectionProps<ScopeApplication> = {
    isCreateMode: true,
    user: { userType, token },
    disabled,
    control,
    errors,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    clearErrors,
    registerCustomError: (name: string, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
  };

  return (
    <>
      <div className={classes.root}>
        <ModuleAppBar
          breadcrumbs={[
            {
              label: orgState && orgState.org ? `${orgState.org.name}` : "",
              to: `${itemId}`,
            },
            {
              label: "Scope of Recognition Applications",
              to: `${itemId}/scope`,
            },
            { label: "New" },
          ]}
        >
          <AppBarSaveButton
            lifecycleLabels={[
              "Save as draft",
              "Saving as draft",
              "Saved as draft",
            ]}
            isSubmitting={createState.status === "CREATING"}
            isSubmitted={createState.lastCrudResult.isSuccess}
            onClick={handleSubmit(onSave)}
            disabled={disabled}
            Icon={Save}
            dataTest="save"
          />
          <AppBarSaveButton
            lifecycleLabels={["Submit", "Submitting", "Submitted"]}
            isSubmitting={createState.status === "SUBMITTING"}
            isSubmitted={createState.lastCrudResult.isSuccess}
            onClick={handleSubmit(onSubmit)}
            disabled={disabled || !canSubmit}
            Icon={Send}
            dataTest="submit-application"
          />
        </ModuleAppBar>

        <Grid container spacing={3}>
          <Grid item md={8} xs={12}>
            <form>
              <h6 className={classes.formHeading} style={{ marginTop: "0" }}>
                Application title
              </h6>
              <Paper className={classes.formSection}>
                <ApplicationTitle {...sectionProps} />
              </Paper>

              <h6 className={classes.formHeading}>Question 1</h6>
              <Paper className={classes.formSection}>
                <QuestionOne {...sectionProps} />
              </Paper>

              <h6 className={classes.formHeading}>Question 2</h6>
              <Paper className={classes.formSection}>
                <QuestionTwo {...sectionProps} />
              </Paper>

              <h6 className={classes.formHeading}>Question 3</h6>
              <Paper className={classes.formSection}>
                <QuestionThree {...sectionProps} />
              </Paper>

              <h6 className={classes.formHeading}>Question 4</h6>
              <Paper className={classes.formSection}>
                <QuestionFour {...sectionProps} />
              </Paper>
            </form>
          </Grid>
          <Grid item md={4} xs={12}>
            <NewApplicationContent />
          </Grid>
        </Grid>
      </div>
    </>
  );
};
